import React, {
    createContext,
    useState,
    ReactNode,
    useContext,
    useEffect,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import imageCompression from 'browser-image-compression'
import { FileContent, FileError, useFilePicker } from 'use-file-picker'

import {
    AttachmentType,
    CreateInternalChatMessageAttachmentInput,
} from '~graphql/generated/graphql'
import { getAttachmentType } from '~utils/helpers'
import { useToast } from '@chakra-ui/react'
import { SetStateFn } from '~config/constants'
import { uploadMessageAttachment } from '~utils/storage-helpers'

const MAX_FILE_SIZE_MB = 1.6
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

export default function TeamChatAttachmentsProvider({
    teamChatId,
    children,
}: {
    teamChatId: string
    children: ReactNode
}) {
    const toast = useToast()

    const [isProcessing, setIsProcessing] = useState(false)
    const [attachments, setAttachments] = useState<
        CreateInternalChatMessageAttachmentInput[]
    >([])

    const [
        openFileSelector,
        { filesContent, clear, loading: filesLoading, errors },
    ] = useFilePicker({
        multiple: true,
        accept: ['.pdf', 'image/*', 'video/*'],
        maxFileSize: 50,
        readAs: 'DataURL',
        limitFilesConfig: {
            min: 1,
            max: 4,
        },
    })

    async function uploadFile(
        teamChatId: string,
        file: FileContent,
        type: AttachmentType,
    ): Promise<CreateInternalChatMessageAttachmentInput> {
        const { name, content } = file

        try {
            const uniqueName = `${uuidv4()}-${name}`
            const storagePath = `team-chats/${teamChatId}/attachments`
            const uploadedUrl = await uploadMessageAttachment(
                storagePath,
                uniqueName,
                content,
            )

            if (!uploadedUrl) {
                throw new Error('Failed to upload attachment')
            }

            return {
                filename: name,
                type,
                url: uploadedUrl.downloadUrl,
            }
        } catch (error) {
            console.error('Error uploading attachment:', name, error)
            toast({
                title: 'Failed to upload attachment',
                description: `Failed to upload ${name}. Please try again or upload a different file.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
            })
            throw error
        }
    }

    async function uploadImage(
        teamChatId: string,
        file: FileContent,
    ): Promise<CreateInternalChatMessageAttachmentInput> {
        let { name, content } = file

        try {
            if (content.length > MAX_FILE_SIZE_BYTES) {
                const response = await fetch(content)
                const blob = (await response.blob()) as File

                const options = {
                    maxSizeMB: MAX_FILE_SIZE_MB,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                }
                const compressedBlob = await imageCompression(blob, options)
                const base64 =
                    await imageCompression.getDataUrlFromFile(compressedBlob)

                if (name.toLowerCase().endsWith('.webp')) {
                    name = name.replace(/\.webp$/i, '.png')
                }

                const uniqueName = `${uuidv4()}-${name}`
                const storagePath = `team-chats/${teamChatId}/attachments`
                const uploadedUrl = await uploadMessageAttachment(
                    storagePath,
                    uniqueName,
                    base64,
                )

                if (!uploadedUrl) {
                    throw new Error('Failed to upload image')
                }

                return {
                    filename: name,
                    type: AttachmentType.Image,
                    url: uploadedUrl.downloadUrl,
                }
            } else {
                const uniqueName = `${uuidv4()}-${name}`
                const storagePath = `team-chats/${teamChatId}/attachments`
                const uploadedUrl = await uploadMessageAttachment(
                    storagePath,
                    uniqueName,
                    content,
                )

                if (!uploadedUrl) {
                    throw new Error('Failed to upload image')
                }

                return {
                    filename: name,
                    type: AttachmentType.Image,
                    url: uploadedUrl.downloadUrl,
                }
            }
        } catch (error) {
            console.error('Error uploading image:', file.name, error)
            toast({
                title: 'Failed to upload image',
                description: `Failed to upload ${file.name}. Please try again or upload a different file.`,
            })
            throw error
        }
    }

    async function handleProcessFile(
        teamChatId: string,
        file: FileContent,
    ): Promise<CreateInternalChatMessageAttachmentInput | null> {
        try {
            const type = getAttachmentType(file.name)

            if (type === AttachmentType.Video) {
                return await uploadFile(teamChatId, file, AttachmentType.Video)
            } else if (type === AttachmentType.Image) {
                return await uploadImage(teamChatId, file)
            } else if (type === AttachmentType.Pdf) {
                return await uploadFile(teamChatId, file, AttachmentType.Pdf)
            } else {
                throw new Error(`Unsupported file type: ${file.name}`)
            }
        } catch (error) {
            console.error('Error processing file:', file.name, error)
            return null
        }
    }

    useEffect(() => {
        const processFiles = async () => {
            if (!filesContent.length || filesLoading) return

            setIsProcessing(true)

            const newFilesContent = filesContent.filter(
                fc =>
                    !attachments.some(
                        attachment => attachment.filename === fc.name,
                    ),
            )

            if (!newFilesContent.length) {
                clear()
                return
            }

            const processedFilesPromises = newFilesContent.map(file =>
                handleProcessFile(teamChatId, file),
            )

            const results = await Promise.all(processedFilesPromises)
            const successfulUploads = results.filter(
                result => result !== null,
            ) as CreateInternalChatMessageAttachmentInput[]

            if (results.length !== successfulUploads.length) {
                const failedCount = results.length - successfulUploads.length
                toast({
                    title: 'Upload Error',
                    description: `${failedCount} file(s) failed to upload or process. Please try again.`,
                })
            }

            if (successfulUploads.length > 0) {
                setAttachments(prev => [...prev, ...successfulUploads])
            }

            clear()
            setIsProcessing(false)
        }

        processFiles()
    }, [
        filesContent,
        filesLoading,
        attachments,
        teamChatId,
        clear,
        setAttachments,
    ])

    return (
        <TeamChatAttachmentsContext.Provider
            value={{
                attachments,
                setAttachments,
                isProcessing,
                errors,
                openFileSelector,
            }}
        >
            {children}
        </TeamChatAttachmentsContext.Provider>
    )
}

interface TeamChatAttachmentsContextType {
    attachments: CreateInternalChatMessageAttachmentInput[]
    setAttachments: SetStateFn<CreateInternalChatMessageAttachmentInput[]>
    isProcessing: boolean
    errors: FileError[]
    openFileSelector: () => void
}

const TeamChatAttachmentsContext =
    createContext<TeamChatAttachmentsContextType>({
        attachments: [],
        setAttachments: () => { },
        isProcessing: false,
        errors: [],
        openFileSelector: () => { },
    })

export function useTeamChatAttachments() {
    return useContext(TeamChatAttachmentsContext)
}
